import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../config';
import { getOrganizationContext } from '../orgContext';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
} from './components/AdminUiPrimitives';
import {
  IconArchive,
  IconBook,
  IconCheckCircle,
  IconGeneral,
  IconMetadata,
  IconRelationships,
} from './components/AdminIcons';

interface MyOrganization {
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  isDefault: boolean;
  roles: string[];
}

interface OrganizationSummary {
  organizationId: number;
  name: string;
  slug: string;
}

interface OrganizationMember {
  membershipId: number;
  userId: number;
  auth0Sub: string;
  email: string;
  status: string;
  isDefault: boolean;
  roles: string[];
}

interface OrganizationUserOption {
  userId: number;
  auth0Sub: string;
  email: string;
  isMember: boolean;
  membershipStatus: string | null;
}

const STATUS_OPTIONS = ['Invited', 'Active', 'Suspended', 'Left'];
const ROLE_OPTIONS = ['Owner', 'Principal', 'Teacher', 'Parent', 'Student'];
const hasOwnerRole = (roles: string[]): boolean => roles.some(role => role.trim().toLowerCase() === 'owner');
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

type SortKey = 'member' | 'status' | 'roles';

interface MembershipRow {
  id: number;
  member: string;
  status: string;
  roles: string;
  membership: OrganizationMember;
}

const DEFAULT_PAGE_SIZE = 10;

const ManageOrganizationMemberships: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [myOrganizations, setMyOrganizations] = useState<MyOrganization[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<OrganizationSummary[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [userOptions, setUserOptions] = useState<OrganizationUserOption[]>([]);
  const [selectedUserSub, setSelectedUserSub] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [provisionEmail, setProvisionEmail] = useState('');
  const [provisionFirstName, setProvisionFirstName] = useState('');
  const [provisionLastName, setProvisionLastName] = useState('');
  const [provisionRole, setProvisionRole] = useState('Student');
  const [sendProvisionInvite, setSendProvisionInvite] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('member');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');
  const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);
  const [pendingStatus, setPendingStatus] = useState<string>('');

  const isSmallScreen = viewportWidth < 768;
  const isNarrowLayout = viewportWidth < 1140;

  const activeOrganizationContext = useMemo(() => getOrganizationContext(), []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const requestedOrganizationId = useMemo(() => {
    const value = searchParams.get('organizationId');
    const parsed = value ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getManagementToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`
        }
      });
    },
    [getManagementToken]
  );

  const organizationOptions = useMemo(
    () => {
      if (isSuperAdmin && allOrganizations.length > 0) {
        return allOrganizations.map(o => ({ id: o.organizationId, name: o.name, slug: o.slug }));
      }

      return myOrganizations
        .filter(o => hasOwnerRole(o.roles))
        .map(o => ({ id: o.organizationId, name: o.organizationName, slug: o.organizationSlug }));
    },
    [allOrganizations, isSuperAdmin, myOrganizations]
  );

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const myResponse = await withAuth({ method: 'get', url: `${apiBaseUrl}/my` });
      const platformRolesResponse = await withAuth({ method: 'get', url: `${apiBaseUrl}/platform-user-roles` }).catch(() => null);
      const canUseSuperAdminTools = !!platformRolesResponse;
      const allResponse = canUseSuperAdminTools
        ? await withAuth({ method: 'get', url: `${apiBaseUrl}/discover` }).catch(() => null)
        : null;

      const myOrgs = (myResponse.data ?? []) as MyOrganization[];
      const allOrgs = (allResponse?.data ?? []) as OrganizationSummary[];

      setMyOrganizations(myOrgs);
      setAllOrganizations(allOrgs);
      setIsSuperAdmin(canUseSuperAdminTools);

      const ownerOrganizations = myOrgs.filter(o => hasOwnerRole(o.roles));
      const activeOrganizationId = activeOrganizationContext?.organizationId ?? null;
      const defaultMyOrg = ownerOrganizations.find(o => o.organizationId === activeOrganizationId)?.organizationId
        ?? ownerOrganizations.find(o => o.isDefault)?.organizationId
        ?? ownerOrganizations[0]?.organizationId;
      const resolved = requestedOrganizationId ?? activeOrganizationId ?? defaultMyOrg ?? null;

      setSelectedOrganizationId(prev => prev ?? resolved);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load organizations.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [activeOrganizationContext?.organizationId, apiBaseUrl, requestedOrganizationId, withAuth]);

  const loadMembers = useCallback(
    async (organizationId: number) => {
      try {
        setLoading(true);
        clearMessages();

        const response = await withAuth({ method: 'get', url: `${apiBaseUrl}/${organizationId}/members` });
        setMembers((response.data ?? []) as OrganizationMember[]);
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Failed to load memberships.';
        setError(message);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl, withAuth]
  );

  const loadUserOptions = useCallback(
    async (organizationId: number) => {
      try {
        const response = await withAuth({ method: 'get', url: `${apiBaseUrl}/${organizationId}/user-options` });
        const options = (response.data ?? []) as OrganizationUserOption[];
        setUserOptions(options);
      } catch {
        setUserOptions([]);
      }
    },
    [apiBaseUrl, withAuth]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadOrganizations();
  }, [isAuthenticated, loadOrganizations]);

  useEffect(() => {
    if (!requestedOrganizationId || !isSuperAdmin) {
      return;
    }

    setSelectedOrganizationId(requestedOrganizationId);
  }, [isSuperAdmin, requestedOrganizationId]);

  useEffect(() => {
    if (isSuperAdmin) {
      return;
    }

    if (activeOrganizationContext?.organizationId) {
      setSelectedOrganizationId(activeOrganizationContext.organizationId);
    }
  }, [activeOrganizationContext?.organizationId, isSuperAdmin]);

  useEffect(() => {
    if (!isAuthenticated || !selectedOrganizationId) {
      setMembers([]);
      setUserOptions([]);
      return;
    }

    loadMembers(selectedOrganizationId);

    if (isSuperAdmin) {
      loadUserOptions(selectedOrganizationId);
    } else {
      setUserOptions([]);
    }
  }, [isAuthenticated, isSuperAdmin, selectedOrganizationId, loadMembers, loadUserOptions]);

  const selectedOrganization = useMemo(
    () => organizationOptions.find(o => o.id === selectedOrganizationId) ?? null,
    [organizationOptions, selectedOrganizationId]
  );

  const membershipRows = useMemo<MembershipRow[]>(
    () =>
      members.map(member => ({
        id: member.membershipId,
        member: member.email || member.auth0Sub || '-',
        status: member.status,
        roles: member.roles.join(', ') || 'None',
        membership: member,
      })),
    [members]
  );

  const selectedMembership = useMemo(
    () => members.find(member => member.membershipId === selectedMembershipId) ?? null,
    [members, selectedMembershipId]
  );

  useEffect(() => {
    if (selectedMembership) {
      setPendingStatus(selectedMembership.status);
      return;
    }

    setPendingStatus('');
  }, [selectedMembership]);

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return membershipRows.filter(row => {
      if (query) {
        const haystack = [
          row.member,
          row.membership.auth0Sub,
          row.status,
          row.roles,
          row.membership.userId,
          row.membership.membershipId,
        ]
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(query)) {
          return false;
        }
      }

      if (statusFilter === 'default') {
        return row.membership.isDefault;
      }

      if (statusFilter !== 'all') {
        return row.status.toLowerCase() === statusFilter;
      }

      return true;
    });
  }, [membershipRows, searchValue, statusFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const direction = sortDirection === 'asc' ? 1 : -1;

    sorted.sort((first, second) => {
      const left = String(first[sortKey]).toLowerCase();
      const right = String(second[sortKey]).toLowerCase();

      if (left < right) {
        return -1 * direction;
      }

      if (left > right) {
        return 1 * direction;
      }

      return 0;
    });

    return sorted;
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const selectableUsers = useMemo(
    () => userOptions.filter(u => !u.isMember),
    [userOptions]
  );

  const inviteEmailTrimmed = inviteEmail.trim();
  const inviteEmailValid = isValidEmail(inviteEmailTrimmed);
  const provisionEmailTrimmed = provisionEmail.trim();
  const provisionEmailValid = isValidEmail(provisionEmailTrimmed);

  const handleAddMember = async () => {
    if (!selectedOrganizationId) {
      setError('Choose an organization first.');
      return;
    }

    if (!selectedUserSub.trim()) {
      setError('Please choose a user.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members`,
        data: { auth0Sub: selectedUserSub.trim() }
      });

      setSelectedUserSub('');
      setNotice('Membership added or reactivated.');
      await loadMembers(selectedOrganizationId);
      await loadUserOptions(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to add membership.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleInviteByEmail = async () => {
    if (!selectedOrganizationId) {
      setError('Choose an organization first.');
      return;
    }

    const email = inviteEmailTrimmed;
    if (!email) {
      setError('Please enter an email.');
      return;
    }

    if (!inviteEmailValid) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/invites`,
        data: { email }
      });

      setInviteEmail('');
      setNotice('Invite sent successfully.');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to send invite.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleProvisionMember = async () => {
    if (!selectedOrganizationId) {
      setError('Choose an organization first.');
      return;
    }

    const email = provisionEmailTrimmed;
    if (!email) {
      setError('Please enter an email to provision.');
      return;
    }

    if (!provisionEmailValid) {
      setError('Please enter a valid email address for provisioning.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const response = await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/provision`,
        data: {
          email,
          firstName: provisionFirstName.trim() || null,
          lastName: provisionLastName.trim() || null,
          roleKey: provisionRole,
          sendInviteEmail: sendProvisionInvite,
        }
      });

      const data = response.data as { message?: string } | undefined;
      setNotice(data?.message || 'User provisioned successfully.');
      setProvisionEmail('');
      setProvisionFirstName('');
      setProvisionLastName('');
      setProvisionRole('Student');
      setSendProvisionInvite(true);

      await loadMembers(selectedOrganizationId);
      await loadUserOptions(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to provision member.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAllUsers = async () => {
    if (!selectedOrganizationId) {
      setError('Choose an organization first.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const response = await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/add-all-users`
      });

      const data = response.data as { added?: number; reactivated?: number } | undefined;
      setNotice(`Processed all users. Added: ${data?.added ?? 0}, Reactivated: ${data?.reactivated ?? 0}.`);
      await loadMembers(selectedOrganizationId);
      await loadUserOptions(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to process all users.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (membershipId: number, status: string) => {
    if (!selectedOrganizationId) {
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'patch',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/${membershipId}/status`,
        data: { status }
      });

      setNotice('Membership status updated.');
      await loadMembers(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to update membership status.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleManageRoles = (membership: OrganizationMember) => {
    if (!selectedOrganizationId) {
      return;
    }

    const params = new URLSearchParams({
      organizationId: String(selectedOrganizationId),
      membershipId: String(membership.membershipId),
      userId: String(membership.userId)
    });

    navigate(`/manage-organization-membership-roles?${params.toString()}`);
  };

  const handleSelectMember = (member: OrganizationMember) => {
    setSelectedMembershipId(member.membershipId);
    setPendingStatus(member.status);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const handleSort = (columnKey: string) => {
    if (!['member', 'status', 'roles'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const columns = useMemo<DataGridColumn<MembershipRow>[]>(() => {
    const items: DataGridColumn<MembershipRow>[] = [
      {
        key: 'member',
        label: 'Member',
        sortable: true,
        width: isSmallScreen ? '56%' : '34%',
        render: (row) => (
          <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.member}</div>
          </div>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: isSmallScreen ? '24%' : '18%',
        render: (row) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 999,
              border: '1px solid #d1ddf3',
              color: '#3a5686',
              background: '#f4f8ff',
              padding: '2px 8px',
              fontSize: '0.73rem',
              fontWeight: 700,
            }}
          >
            {row.status}
          </span>
        ),
      },
    ];

    if (!isSmallScreen) {
      items.push({
        key: 'roles',
        label: 'Roles',
        sortable: true,
        width: '32%',
        render: (row) => row.roles,
      });
    }

    return items;
  }, [isSmallScreen]);

  const rowActions: DataGridRowAction<MembershipRow>[] = [
    {
      key: 'activate',
      label: 'Activate member',
      icon: <IconCheckCircle size={14} />,
      onClick: (row) => {
        void handleUpdateStatus(row.membership.membershipId, 'Active');
      },
      isDisabled: (row) => saving || row.membership.status.toLowerCase() === 'active',
    },
    {
      key: 'suspend',
      label: 'Suspend member',
      icon: <IconArchive size={14} />,
      tone: 'destructive',
      onClick: (row) => {
        void handleUpdateStatus(row.membership.membershipId, 'Suspended');
      },
      isDisabled: (row) => saving || row.membership.status.toLowerCase() === 'suspended',
    },
    {
      key: 'roles',
      label: 'Manage roles',
      icon: <IconRelationships size={14} />,
      onClick: (row) => handleManageRoles(row.membership),
      isDisabled: () => saving,
    },
  ];

  const filterConfigs = useMemo<FilterConfig[]>(
    () => [
      {
        key: 'status',
        label: 'Status',
        value: statusFilter,
        onChange: (value) => {
          setStatusFilter(value);
          setPage(1);
        },
        options: [
          { value: 'all', label: 'All statuses' },
          { value: 'active', label: 'Active' },
          { value: 'invited', label: 'Invited' },
          { value: 'suspended', label: 'Suspended' },
          { value: 'left', label: 'Left' },
          { value: 'default', label: 'Default only' },
        ],
      },
    ],
    [statusFilter]
  );

  const actions: HeaderAction[] = [
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary',
      onClick: () => {
        void loadOrganizations();
        if (selectedOrganizationId) {
          void loadMembers(selectedOrganizationId);
          if (isSuperAdmin) {
            void loadUserOptions(selectedOrganizationId);
          }
        }
      },
      disabled: saving,
    },
  ];

  const totalMembers = members.length;
  const activeMembers = members.filter(member => member.status.toLowerCase() === 'active').length;
  const invitedMembers = members.filter(member => member.status.toLowerCase() === 'invited').length;
  const suspendedMembers = members.filter(member => member.status.toLowerCase() === 'suspended').length;

  const showListPanel = !isSmallScreen || mobilePane === 'list';
  const showDetailsPanel = !isSmallScreen || mobilePane === 'details';

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Organization Memberships</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to manage memberships.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Organizations', to: '/manage-organizations' },
          {
            label: 'Memberships',
            onClick: () => {
              if (isSmallScreen) {
                setMobilePane('list');
              }
            },
          },
          {
            label: selectedOrganization ? `${selectedOrganization.name} Members` : 'Memberships',
            isCurrent: true,
          },
        ]}
      />

      <AdminPageHeader
        title="Organization Memberships"
        subtitle={`${totalMembers} members • ${selectedIds.length} selected`}
        actions={actions}
      />

      {(error || notice) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || notice}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <AdminStatCard label="Total" value={totalMembers} icon={<IconBook size={15} />} />
        <AdminStatCard label="Active" value={activeMembers} icon={<IconCheckCircle size={15} />} accent="active" />
        <AdminStatCard label="Invited" value={invitedMembers} icon={<IconMetadata size={15} />} accent="draft" />
        <AdminStatCard label="Suspended" value={suspendedMembers} icon={<IconArchive size={15} />} accent="archived" />
      </div>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value);
          setPage(1);
        }}
        searchPlaceholder="Search by member, status, role, auth0 id..."
        filters={filterConfigs}
      />

      {isSmallScreen && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid #d8e3f6',
            borderRadius: 11,
            background: '#f6f9ff',
            padding: 4,
            width: 'fit-content',
          }}
        >
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'list' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('details')}
          >
            Details
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowLayout ? '1fr' : 'minmax(420px, 58%) minmax(320px, 42%)',
          gap: 12,
          minHeight: isNarrowLayout ? 'auto' : 560,
        }}
      >
        {showListPanel && (
          <div style={{ minHeight: 0 }}>
            {loading ? (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>
                Loading memberships...
              </div>
            ) : (
              <AdminDataGrid<MembershipRow>
                rows={pagedRows}
                columns={columns}
                selectedRowId={selectedMembershipId}
                onRowClick={(row) => handleSelectMember(row.membership)}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                showSelection={!isSmallScreen}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={sortedRows.length}
                onPageChange={setPage}
                emptyMessage={selectedOrganizationId ? 'No memberships found for this organization.' : 'Loading memberships...'}
                rowActions={rowActions}
              />
            )}
          </div>
        )}

        {showDetailsPanel && (
          <div
            style={{
              border: '1px solid #dfe8f8',
              borderRadius: 12,
              background: '#fff',
              boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              {isSmallScreen && (
                <button
                  type="button"
                  className="amd-btn amd-btn-ghost"
                  style={{ marginBottom: 8 }}
                  onClick={() => setMobilePane('list')}
                >
                  Back to List
                </button>
              )}
              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: '1.02rem',
                  fontWeight: 800,
                  color: '#173159',
                  background: selectedMembership
                    ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)'
                    : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                  border: selectedMembership ? '1px solid #cfe0fb' : '1px solid #d9e2ef',
                  borderRadius: 10,
                  padding: '9px 11px',
                }}
              >
                {selectedMembership
                  ? `Membership #${selectedMembership.membershipId}`
                  : selectedOrganization
                  ? `${selectedOrganization.name} Memberships`
                  : 'Memberships'}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ display: 'grid', gap: 18 }}>
                <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconGeneral size={14} />} title="Provision User Before Signup" />
                  <div style={{ color: '#5c6f92', fontSize: '0.8rem' }}>
                    Create an invited member now. When they sign in with the same verified email later, access activates automatically.
                  </div>
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(2, minmax(0, 1fr))' }}>
                    <input
                      type="email"
                      placeholder="User email"
                      value={provisionEmail}
                      onChange={event => setProvisionEmail(event.target.value)}
                      onBlur={() => setProvisionEmail(prev => prev.trim())}
                      maxLength={254}
                      aria-invalid={provisionEmailTrimmed.length > 0 && !provisionEmailValid}
                    />
                    <select
                      value={provisionRole}
                      onChange={event => setProvisionRole(event.target.value)}
                    >
                      {ROLE_OPTIONS.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="First name (optional)"
                      value={provisionFirstName}
                      onChange={event => setProvisionFirstName(event.target.value)}
                      maxLength={100}
                    />
                    <input
                      type="text"
                      placeholder="Last name (optional)"
                      value={provisionLastName}
                      onChange={event => setProvisionLastName(event.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#334155', fontSize: '0.8rem' }}>
                    <input
                      type="checkbox"
                      checked={sendProvisionInvite}
                      onChange={event => setSendProvisionInvite(event.target.checked)}
                    />
                    Send invite email now
                  </label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="amd-btn amd-btn-primary"
                      onClick={() => {
                        void handleProvisionMember();
                      }}
                      disabled={saving || !selectedOrganizationId || !provisionEmailTrimmed || !provisionEmailValid}
                    >
                      Provision Member
                    </button>
                    <button
                      type="button"
                      className="amd-btn amd-btn-ghost"
                      onClick={() => {
                        void handleInviteByEmail();
                      }}
                      disabled={saving || !selectedOrganizationId || !inviteEmailTrimmed || !inviteEmailValid}
                    >
                      Send Classic Invite
                    </button>
                    <input
                      type="email"
                      placeholder="Invite-only email (optional)"
                      value={inviteEmail}
                      onChange={event => setInviteEmail(event.target.value)}
                      onBlur={() => setInviteEmail(prev => prev.trim())}
                      maxLength={254}
                      style={{ minWidth: isSmallScreen ? '100%' : 240 }}
                      aria-invalid={inviteEmailTrimmed.length > 0 && !inviteEmailValid}
                    />
                  </div>
                  {provisionEmailTrimmed.length > 0 && !provisionEmailValid && (
                    <div style={{ color: '#b91c1c', fontSize: '0.77rem' }}>
                      Enter a valid provision email (example: learner@school.com).
                    </div>
                  )}
                  {inviteEmailTrimmed.length > 0 && !inviteEmailValid && (
                    <div style={{ color: '#b91c1c', fontSize: '0.77rem' }}>
                      Invite-only email is invalid.
                    </div>
                  )}
                </section>

                <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconRelationships size={14} />} title="Add Existing User" />
                  {isSuperAdmin ? (
                    <>
                      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isSmallScreen ? '1fr' : 'minmax(0, 1fr) auto' }}>
                        <select
                          value={selectedUserSub}
                          onChange={event => setSelectedUserSub(event.target.value)}
                        >
                          <option value="">Select user</option>
                          {selectableUsers.map(user => (
                            <option key={user.userId} value={user.auth0Sub}>
                              {(user.email || user.auth0Sub) + ` (${user.auth0Sub})`}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="amd-btn amd-btn-secondary"
                          onClick={() => {
                            void handleAddMember();
                          }}
                          disabled={saving || !selectedOrganizationId || !selectedUserSub}
                        >
                          Add Membership
                        </button>
                      </div>
                      {selectedOrganization?.slug === 'exammasters' && (
                        <button
                          type="button"
                          className="amd-btn amd-btn-ghost"
                          onClick={() => {
                            void handleAddAllUsers();
                          }}
                          disabled={saving || !selectedOrganizationId}
                          style={{ justifySelf: 'start' }}
                        >
                          Add All Existing Users To ExamMasters
                        </button>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: '0.82rem', color: '#5c6f92' }}>
                      Global user list is hidden for non-superadmin users.
                    </div>
                  )}
                </section>

                <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconMetadata size={14} />} title="Membership Actions" />
                  {!selectedMembership ? (
                    <div
                      style={{
                        minHeight: 150,
                        border: '1px dashed #c9d6ef',
                        borderRadius: 12,
                        background: '#f8fbff',
                        display: 'grid',
                        placeContent: 'center',
                        textAlign: 'center',
                        color: '#4f658f',
                        gap: 8,
                        padding: 16,
                      }}
                    >
                      <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconBook size={24} /></div>
                      <div style={{ fontWeight: 700 }}>Select a membership from the list</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gap: 6, fontSize: '0.82rem', color: '#586f96' }}>
                        <div>Email: <strong>{selectedMembership.email || '-'}</strong></div>
                        <div>Default Membership: <strong>{selectedMembership.isDefault ? 'Yes' : 'No'}</strong></div>
                        <div>Roles: <strong>{selectedMembership.roles.join(', ') || 'None'}</strong></div>
                      </div>
                      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isSmallScreen ? '1fr' : 'minmax(0, 1fr) auto auto' }}>
                        <select value={pendingStatus} onChange={event => setPendingStatus(event.target.value)}>
                          {STATUS_OPTIONS.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="amd-btn amd-btn-secondary"
                          onClick={() => {
                            if (pendingStatus) {
                              void handleUpdateStatus(selectedMembership.membershipId, pendingStatus);
                            }
                          }}
                          disabled={saving || !pendingStatus || pendingStatus === selectedMembership.status}
                        >
                          Update Status
                        </button>
                      </div>
                    </>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageOrganizationMemberships;
